'use client'

import { useCallback, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { signOut, useSession } from 'next-auth/react'
import { toast } from '@/components/ui/toast'

const IDLE_TIMEOUT_MS = 15 * 60 * 1000
const WARNING_BEFORE_MS = 60 * 1000
const ACTIVITY_THROTTLE_MS = 3 * 1000
const CHECK_INTERVAL_MS = 1000

const STORAGE_ACTIVITY_KEY = 'iris_last_activity'
const STORAGE_LOGOUT_KEY = 'iris_idle_logout'

const AUTH_ROUTE_PREFIX = '/auth'

const WARNING_TOAST_ID = 'idle-timeout-warning'

function getBasePath(): string {
  return process.env.NEXT_PUBLIC_BASE_PATH || ''
}

export function IdleTimeout() {
  const { status } = useSession()
  const pathname = usePathname()
  const lastActivityRef = useRef(Date.now())
  const warningShownRef = useRef(false)
  const throttleUntilRef = useRef(0)

  const isAuthRoute = pathname?.startsWith(AUTH_ROUTE_PREFIX) ?? false
  const isActive = status === 'authenticated' && !isAuthRoute

  const dismissWarning = useCallback(() => {
    toast.dismiss(WARNING_TOAST_ID)
    warningShownRef.current = false
  }, [])

  const performLogout = useCallback(() => {
    dismissWarning()
    const basePath = getBasePath()
    try {
      localStorage.setItem(STORAGE_LOGOUT_KEY, String(Date.now()))
    } catch {
      // localStorage may be unavailable in private mode — still sign out locally
    }
    signOut({ callbackUrl: `${basePath}/auth/signin` })
  }, [dismissWarning])

  const recordActivity = useCallback(
    (source: 'local' | 'remote' = 'local') => {
      const now = Date.now()
      lastActivityRef.current = now
      dismissWarning()

      if (source === 'local') {
        try {
          localStorage.setItem(STORAGE_ACTIVITY_KEY, String(now))
        } catch {
          // Ignore storage errors
        }
      }
    },
    [dismissWarning]
  )

  const handleUserActivity = useCallback(() => {
    const now = Date.now()
    if (now < throttleUntilRef.current) {
      return
    }
    throttleUntilRef.current = now + ACTIVITY_THROTTLE_MS
    recordActivity('local')
  }, [recordActivity])

  const showWarningToast = useCallback(() => {
    if (warningShownRef.current) {
      return
    }
    warningShownRef.current = true
    toast.warning('Session expiring soon', {
      description:
        'You will be signed out in about 1 minute due to inactivity. Move your mouse or press a key to stay signed in.',
      duration: WARNING_BEFORE_MS,
      id: WARNING_TOAST_ID,
    })
  }, [])

  useEffect(() => {
    if (!isActive) {
      dismissWarning()
      return
    }

    recordActivity('local')

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
    ]

    for (const event of events) {
      window.addEventListener(event, handleUserActivity, { passive: true })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleUserActivity()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_ACTIVITY_KEY && event.newValue) {
        const remoteTs = Number(event.newValue)
        if (!Number.isNaN(remoteTs) && remoteTs > lastActivityRef.current) {
          recordActivity('remote')
        }
      }
      if (event.key === STORAGE_LOGOUT_KEY && event.newValue) {
        performLogout()
      }
    }
    window.addEventListener('storage', handleStorage)

    const intervalId = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current

      if (idleMs >= IDLE_TIMEOUT_MS) {
        performLogout()
        return
      }

      if (idleMs >= IDLE_TIMEOUT_MS - WARNING_BEFORE_MS) {
        showWarningToast()
      }
    }, CHECK_INTERVAL_MS)

    return () => {
      for (const event of events) {
        window.removeEventListener(event, handleUserActivity)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('storage', handleStorage)
      window.clearInterval(intervalId)
      dismissWarning()
    }
  }, [
    dismissWarning,
    handleUserActivity,
    isActive,
    performLogout,
    recordActivity,
    showWarningToast,
  ])

  return null
}
